import { useState, useEffect, useCallback } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import uuid from "react-uuid";
import Loading from "../../../common/components/loading/loading";
import { getQualityAttributeValues } from "../../../common/services/products/qualityAttributeValueService";
import SearchBox from "../../../common/components/search/searchBox";
import QualityAttributeValuesTable from "./qualityAttributeValuesTable";
import { withTranslation } from "react-i18next";

const QualityAttributeValues = ({ onClick, t, allowEdit }) => {
  // State management with useState hook
  const [qualityAttributeValues, setQualityAttributeValues] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      const data = await getQualityAttributeValues();
      setQualityAttributeValues(data);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  // Event handlers
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleSort = useCallback((newSortColumn) => {
    setSortColumn(newSortColumn);
  }, []);

  const handleToggleActiveOnly = useCallback(() => {
    setActiveOnly((prevActiveOnly) => !prevActiveOnly);
  }, []);

  const handleQualityAttributeValueUpdated = useCallback(
    (qualityAttributeValue) => {
      setQualityAttributeValues((prevValues) => {
        const updatedValues = [...prevValues];
        const index = updatedValues.findIndex(
          (u) =>
            u.qualityAttributeValueId ===
            qualityAttributeValue.qualityAttributeValueId
        );

        if (index > -1) {
          updatedValues.splice(index, 1);
        }

        return [...updatedValues, qualityAttributeValue];
      });
    },
    []
  );

  const handleQualityAttributeValueSelected = useCallback(
    (qualityAttributeValue, isNew = false) => {
      // Inject the callback function before calling back to the onClick event
      onClick(qualityAttributeValue, handleQualityAttributeValueUpdated, isNew);
    },
    [onClick, handleQualityAttributeValueUpdated]
  );

  // Data processing function
  const getFilteredData = useCallback(() => {
    let filtered = qualityAttributeValues;

    if (activeOnly) filtered = filtered.filter((u) => u.active);

    if (searchQuery)
      filtered = filtered.filter(
        (u) =>
          u.value != null &&
          u.value.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);

    return { totalCount: filtered.length, data: sorted };
  }, [qualityAttributeValues, activeOnly, searchQuery, sortColumn]);

  if (isLoading) return <Loading />;

  const { totalCount, data: pagedQualityAttributeValues } = getFilteredData();

  const newQualityAttributeValue = {
    qualityAttributeValueId: uuid(),
    value: t("NewQualityAttributeValue"),
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={activeOnly}
            value={activeOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
        <div className="col">
          {allowEdit && (
            <button
              onClick={() =>
                handleQualityAttributeValueSelected(
                  newQualityAttributeValue,
                  true
                )
              }
              className="btn btn-sm btn-primary float-sm-right"
            >
              {t("AddQualityAttributeValue")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedQualityAttributeValue", { count: totalCount })}</p>
      <QualityAttributeValuesTable
        qualityAttributeValues={pagedQualityAttributeValues}
        sortColumn={sortColumn}
        onSort={handleSort}
        onRowClick={handleQualityAttributeValueSelected}
      />
    </div>
  );
};

export default withTranslation(["products"])(QualityAttributeValues);
